import { useCallback, useEffect, useState } from "react";
import { useNative, usePhone } from "../../../shared/phone/media";
import { Chevron } from "../../../shared/phone/Row";
import { ActionSheet, Sheet } from "../../../shared/phone/Sheet";
import { Icon } from "../../components/ui/Icon";
import { ApiError } from "../../lib/api";
import { productsApi, type ProfileVersion } from "../../lib/productsApi";
import { compareDocs, type LineChange } from "../library/parts";
import { Compare, VersionList, VersionsDrawer, whenOf, type Changes, type Ver } from "../versions/Versions";
import { profileDoc } from "./profileDoc";

const verOf = (v: ProfileVersion): Ver => ({
  id: `v${v.version}`,
  n: v.version,
  label: `Version ${v.version}`,
  mine: v.by === "owner",
  by: v.by === "owner" ? "You" : v.confirmed ? "The agent, confirmed by you" : "The agent",
  at: v.at,
  note: v.note,
});

/** A confirmed profile's versions: who made each, what changed, and Use this version, which takes effect at once. */
export function ProfileVersions({
  productId,
  name,
  onto,
  onClose,
  onUsed,
  say,
}: {
  productId: string;
  name: string;
  /** The confirmed version: going back lands on top of it. */
  onto: number;
  onClose: () => void;
  onUsed: (from: number, now: number) => Promise<void>;
  say: (message: string, done?: boolean) => void;
}) {
  const [list, setList] = useState<ProfileVersion[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [cmp, setCmp] = useState<{ from: Ver; to: Ver } | null>(null);
  const [busy, setBusy] = useState(false);
  const native = useNative();
  useEffect(() => {
    productsApi.versions(productId).then(
      (r) => setList(r.versions),
      () => setFailed(true),
    );
  }, [productId, onto]);
  const load = useCallback(
    async (from: Ver, to: Ver): Promise<Changes> => {
      const [a, b] = await Promise.all([productsApi.version(productId, from.n), productsApi.version(productId, to.n)]);
      if (!a.profile || !b.profile) throw new Error("Not a profile.");
      return compareDocs(profileDoc(a.profile), profileDoc(b.profile));
    },
    [productId],
  );

  // Why: a draft that asked which business is theirs holds no profile to read or use.
  const vers = (list ?? []).filter((v) => !v.question).map(verOf);
  const current = `v${onto}`;
  const next = Math.max(0, ...(list ?? []).map((v) => v.version)) + 1;
  const previous = (v: Ver) => vers.filter((x) => x.n < v.n).at(-1) ?? null;
  const use = async (v: Ver) => {
    setBusy(true);
    try {
      const { version } = await productsApi.useVersion(productId, v.n, onto);
      await onUsed(v.n, version);
    } catch (e) {
      say(e instanceof ApiError ? (/Nothing changed/.test(e.message) ? "Those are the words already in use." : e.message) : "That did not work. Try again.", false);
    } finally {
      setBusy(false);
    }
  };

  if (native)
    return <VersionsNative name={name} vers={vers} current={current} next={next} failed={failed} loading={list === null} busy={busy} load={load} use={use} onClose={onClose} />;

  if (cmp)
    return (
      <Compare
        versions={vers}
        from={cmp.from}
        to={cmp.to}
        load={load}
        onPick={(from, to) => setCmp({ from, to })}
        onClose={() => setCmp(null)}
        foot={
          cmp.to.id !== current && (
            <>
              <span className="note">Going back makes a new version. Nothing is deleted.</span>
              <button type="button" className="btn sm" disabled={busy} onClick={() => void use(cmp.to)}>
                Use {cmp.to.label.toLowerCase()}
              </button>
            </>
          )
        }
      />
    );
  return (
    <VersionsDrawer title={`${name}'s profile`} onClose={onClose}>
      {failed ? (
        <p className="cm-sum">The versions did not load. Close this and try again.</p>
      ) : list === null ? (
        <p className="cm-sum">Loading the versions…</p>
      ) : (
        <VersionList
          head={false}
          versions={vers}
          current={current}
          previous={previous}
          ask={(v) => ({
            q: `Use the words of ${v.label.toLowerCase()}?`,
            body: `They become version ${next}, and every goal for ${name} reads them from now. Nothing is deleted.`,
            button: "Use these words",
          })}
          acts={{ busy, compare: (from, to) => setCmp({ from, to }), use }}
          where="profile"
        />
      )}
    </VersionsDrawer>
  );
}

/** A block's words on one line: no bold marks, its list lines joined. */
const plainWords = (t: string) =>
  t
    .split("\n")
    .map((l) => l.replace(/^\s*(?:[-*]|\d+[.)])\s+/, "").replace(/\*\*|__|`/g, "").trim())
    .filter(Boolean)
    .join(" · ");
const quote = (t: string) => `“${plainWords(t)}”`;

/** One change in a line of words, in plain words. */
function changeWords(c: LineChange): string {
  if (c.k === "add") return `Added ${quote(c.b.text)}`;
  if (c.k === "rm") return `Taken out ${quote(c.a.text)}`;
  return `Now reads ${quote(c.b.text)}`;
}

const SHOWN_CHANGES = 3;

/**
 * The versions on a phone or an iPad, in a sheet: newest first, the one in use marked. An older one opens
 * what changed since its words, and Use these words, which asks first.
 */
function VersionsNative({
  name,
  vers,
  current,
  next,
  failed,
  loading,
  busy,
  load,
  use,
  onClose,
}: {
  name: string;
  vers: Ver[];
  current: string;
  next: number;
  failed: boolean;
  loading: boolean;
  busy: boolean;
  load: (from: Ver, to: Ver) => Promise<Changes>;
  use: (v: Ver) => Promise<void>;
  onClose: () => void;
}) {
  const phone = usePhone();
  const [pick, setPick] = useState<Ver | null>(null);
  const [asking, setAsking] = useState(false);
  const [changes, setChanges] = useState<Changes | null>(null);
  const [cmpErr, setCmpErr] = useState(false);
  const inUse = vers.find((v) => v.id === current) ?? null;
  const pickId = pick?.id;

  useEffect(() => {
    if (!pick || !inUse) return;
    let alive = true;
    setChanges(null);
    setCmpErr(false);
    load(pick, inUse).then(
      (r) => alive && setChanges(r),
      () => alive && setCmpErr(true),
    );
    return () => {
      alive = false;
    };
    // Why: the versions are built afresh on every render; the same two ids are the same comparison.
  }, [pickId, inUse?.id, load]);

  return (
    <>
      <Sheet
        open
        onClose={onClose}
        title={pick ? pick.label : `${name}'s profile`}
        tall={!!pick && phone}
        left={
          pick ? (
            <button type="button" className="pn-tbtn" onClick={() => setPick(null)}>
              Back
            </button>
          ) : undefined
        }
        right={
          pick ? undefined : (
            <button type="button" className="pn-tbtn strong" onClick={onClose}>
              Done
            </button>
          )
        }
        foot={
          pick ? (
            <button type="button" className="pn-btn pri wide" disabled={busy} onClick={() => setAsking(true)}>
              Use these words
            </button>
          ) : undefined
        }
      >
        {pick ? (
          <>
            <p className="pd-ssub">
              {pick.by}, {whenOf(pick.at)}
            </p>
            <div className="pn-cap">What changed after it</div>
            <div className="pn-grp">
              {cmpErr && (
                <div className="pn-row static wrap">
                  <span className="m">
                    <span className="s">Those versions did not load. Go back and try again.</span>
                  </span>
                </div>
              )}
              {!changes && !cmpErr && (
                <div className="pn-row static">
                  <span className="m">
                    <span className="s">Reading both versions…</span>
                  </span>
                </div>
              )}
              {changes && changes.parts.length === 0 && (
                <div className="pn-row static">
                  <span className="m">
                    <span className="s">The same words as the version in use.</span>
                  </span>
                </div>
              )}
              {changes?.parts.map((c) => (
                <div className="pn-row static wrap pd-vchange" key={c.id}>
                  <span className="m">
                    <span className="t">
                      {c.title}
                      {c.status && <span className="pn-pill">{c.status === "added" ? "New part" : "Taken out"}</span>}
                    </span>
                    {c.changes.slice(0, SHOWN_CHANGES).map((x, k) => (
                      <span className="s" key={k}>
                        {changeWords(x)}
                      </span>
                    ))}
                    {c.changes.length > SHOWN_CHANGES && <span className="s">and {c.changes.length - SHOWN_CHANGES} more</span>}
                  </span>
                </div>
              ))}
            </div>
            <p className="pn-note">Going back makes a new version. Nothing is deleted.</p>
          </>
        ) : (
          <>
            <div className="pn-grp">
              {failed && (
                <div className="pn-row static">
                  <span className="m">
                    <span className="s">The versions did not load. Close this and try again.</span>
                  </span>
                </div>
              )}
              {loading && !failed && (
                <div className="pn-row static">
                  <span className="m">
                    <span className="s">Loading the versions…</span>
                  </span>
                </div>
              )}
              {vers.toReversed().map((v) =>
                v.id === current ? (
                  <div className="pn-row static wrap" key={v.id}>
                    <span className="pn-ico">
                      <Icon name="clock" />
                    </span>
                    <span className="m">
                      <span className="t">{v.label}</span>
                      <span className="s">
                        {v.by}, {whenOf(v.at)}
                      </span>
                    </span>
                    <span className="pn-pill acc">In use</span>
                  </div>
                ) : (
                  <button type="button" className="pn-row wrap" key={v.id} onClick={() => setPick(v)}>
                    <span className="pn-ico plain">
                      <Icon name="clock" />
                    </span>
                    <span className="m">
                      <span className="t">{v.label}</span>
                      <span className="s">
                        {v.by}, {whenOf(v.at)}
                      </span>
                    </span>
                    <Chevron />
                  </button>
                ),
              )}
            </div>
            {vers.length > 1 && <p className="pn-note">Tap an older version to see what changed since, or to go back to its words.</p>}
          </>
        )}
      </Sheet>
      <ActionSheet
        open={asking && !!pick}
        onClose={() => setAsking(false)}
        title={pick ? `Use the words of ${pick.label.toLowerCase()}?` : undefined}
        message={`They become version ${next}, and every goal for ${name} reads them from now. Nothing is deleted.`}
        actions={pick ? [{ label: "Use these words", onSelect: () => void use(pick) }] : []}
      />
    </>
  );
}
